import { Input, Row } from 'reactstrap';
import { useTranslation } from '../../store/context/TranslationContext';
import { useMemo } from 'react';
import { LabelValue } from '../../pages/Form/forms/IndividualPlus/IPlusUserDetailForm';
import { buildAutomationChildTestId, buildAutomationValueTestId, resolveAutomationTestId } from '../InputField/testId';

const sanitizeWrapperClassName = (rawClassName = "") => (
    String(rawClassName || "")
        .split(/\s+/)
        .filter(Boolean)
        .filter((classToken) => !(
            classToken === "d-flex"
            || classToken.startsWith("flex-")
            || classToken.startsWith("justify-content-")
            || classToken.startsWith("align-items-")
        ))
        .join(" ")
);

const normalizeMultiValue = (rawValue) => {
    if (Array.isArray(rawValue)) {
        return rawValue;
    }

    if (typeof rawValue === 'string') {
        const trimmed = rawValue.trim();
        if (!trimmed) {
            return [];
        }
        return trimmed.includes(',')
            ? trimmed.split(',').map((item) => item.trim()).filter(Boolean)
            : [trimmed];
    }

    if (rawValue === null || rawValue === undefined) {
        return [];
    }

    if (typeof rawValue === 'number' || typeof rawValue === 'boolean') {
        return [rawValue];
    }

    return [];
};

const CheckBoxFieldMutiple = ({
    id,
    className,
    optionClassName,
    labelStyle,
    label,
    data = [],
    prevValue,
    value,
    onChange,
    desc,
    error,
    showErr,
    isDisabled,
    required,
    showChanges = false
}) => {
    const { t } = useTranslation();
    const fieldTestId = resolveAutomationTestId(id, label);
    const valueToLabelMap = useMemo(() => Object.fromEntries(data.map(({ value, label }) => [value, label])), [data]);
    const normalizedValue = useMemo(() => normalizeMultiValue(value), [value]);
    const normalizedPrevValue = useMemo(() => {
        if (prevValue?.has && Array.isArray(prevValue?.value)) {
            return prevValue.value;
        }

        return null;
    }, [prevValue?.has, prevValue?.value]);

    const added = useMemo(() => {
        if (!Array.isArray(normalizedPrevValue)) {
            return [];
        }
        return normalizedValue.filter(item => !normalizedPrevValue.includes(item));
    }, [normalizedValue, normalizedPrevValue]);

    const removed = useMemo(() => {
        if (!Array.isArray(normalizedPrevValue)) {
            return [];
        }
        return normalizedPrevValue.filter(item => !normalizedValue.includes(item));
    }, [normalizedValue, normalizedPrevValue]);

    const formatValueList = (arr) => arr.map(val => valueToLabelMap?.[val]).filter(Boolean).join(", ");
    const resolvedWrapperClassName = sanitizeWrapperClassName(className);
    const wrapperClassSuffix = resolvedWrapperClassName ? ` ${resolvedWrapperClassName}` : "";
    const resolvedOptionClassName = optionClassName || "col-12 col-md-6";

    return (
        <div className={`fv__selection-field fv__selection-field--checkbox my-2${wrapperClassSuffix}`} data-testid={fieldTestId}>
            <div className='d-flex'>
                <label style={labelStyle} data-testid={buildAutomationChildTestId(fieldTestId, "label")}>{t(label)}{required && <font color="red">*</font>}</label>
            </div>
            <Row className='fv__checkbox-group g-2 ps-2'>
                {(data ?? [])?.map((item) => {
                    const optionTestId = buildAutomationValueTestId(fieldTestId, item?.value ?? item?.label);
                    return (
                    <div key={item.value} className={`fv__checkbox-option-col ${resolvedOptionClassName}`}>
                        <label
                            htmlFor={optionTestId}
                            data-testid={buildAutomationChildTestId(optionTestId, "label")}
                            className={`fv__checkbox-option ${isDisabled ? "cursor-disabled" : ""}`}
                        >
                            <Input
                                id={optionTestId}
                                data-testid={optionTestId}
                                type="checkbox"
                                checked={normalizedValue.includes(item?.value)}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        onChange([...normalizedValue, item.value]);
                                    } else {
                                        onChange(normalizedValue.filter((v) => v !== item?.value));
                                    }
                                }}
                                disabled={isDisabled}
                                className={`custom-checklist fv__checkbox-option-input ${added?.includes(item?.value) ? "is-changed" : isDisabled ? "is-disabled" : ""}`}
                            />
                            <span className='fv__checkbox-option-label'>
                                {t(item.label)}
                            </span>
                        </label>
                    </div>
                    );
                })}
            </Row>
            {
                showChanges && (removed?.length > 0 || added?.length > 0) ? (
                    <div className='pt-2' data-testid={buildAutomationChildTestId(fieldTestId, "changes")}>
                        {removed?.length > 0 ? <LabelValue label={t("Removed")} value={formatValueList(removed)} /> : null}
                        {added?.length > 0 ? <LabelValue label={t("Added")} value={formatValueList(added)} /> : null}
                    </div>
                ) : null
            }
            {
                showChanges && !removed?.length && !added?.length ? (
                    <div className='fv__field-old-value mt-2' data-testid={buildAutomationChildTestId(fieldTestId, "no-changes")}>
                        {t("No Changes")}
                    </div>
                ) : null
            }
            {desc ? <em className="inputfield__desc d-block mt-2" data-testid={buildAutomationChildTestId(fieldTestId, "description")}>
                {desc ? t(desc) : ""}
            </em> : null}
            {(error?.length && showErr) ? <div className="mt-1 selectfield__error" data-testid={buildAutomationChildTestId(fieldTestId, "error")}>{t(error)}</div> : null}

        </div>
    )
}

export default CheckBoxFieldMutiple
